import React, { useEffect, useRef, useState } from "react";
import { Link, Navigate, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { PickerField, Spinner } from "../../components/lawyer/LawyerUi";
import StripeCardModal from "../../components/user/StripeCardModal";
import FindingLawyer, { SEARCH_SECONDS } from "../../components/call/FindingLawyer";
import CallScreen from "../../components/call/CallScreen";
import MediaPermissionModal from "../../components/call/MediaPermissionModal";
import { ensureMediaPermission, getMediaPermissionState } from "../../components/call/media";
import socketService from "../../services/SocketService";
import { apiErrorMessage, userAuth, userService } from "../../services/userApi";

const STEPS = [
  { n: 1, label: "Your Case" },
  { n: 2, label: "Select Time" },
  { n: 3, label: "Connect" },
];

const isFree = (p) => !!p && (!!p.is_free || Number(p.price) === 0);
const fmtPrice = (p) => (isFree(p) ? "Free" : `${p.currency || "USD"} ${Number(p.price).toFixed(2)}`);

/**
 * "Video Call with Lawyer": broadcasts a consultation to every matching lawyer
 * (same flow as the public /consultation-call page, but with the signed-in
 * session instead of an OTP). A direct call to one lawyer is a different flow,
 * started from their profile (components/call/DirectCall), so ?lawyer=<id>
 * forwards there.
 */
const UserVideoCall = () => {
  const navigate = useNavigate();
  const { profile } = useOutletContext();
  const [params] = useSearchParams();
  const directLawyerId = params.get("lawyer");

  const [step, setStep] = useState(1);
  const [categories, setCategories] = useState([]);
  const [jurisdictions, setJurisdictions] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [jurisdictionId, setJurisdictionId] = useState("");
  const [description, setDescription] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const previousDescription = useRef("");

  const [packages, setPackages] = useState([]);
  const [freeTaken, setFreeTaken] = useState(false);
  const [packagesLoading, setPackagesLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const [checkingMedia, setCheckingMedia] = useState(false);
  const [mediaPrompt, setMediaPrompt] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [booking, setBooking] = useState(false);

  // idle | searching | unavailable | connected
  const [phase, setPhase] = useState("idle");
  const [lawyersFound, setLawyersFound] = useState(false);
  const [activeCall, setActiveCall] = useState(null);
  const phaseRef = useRef("idle");
  const callRef = useRef(null);
  const giveUpTimer = useRef(null);
  phaseRef.current = phase;
  callRef.current = activeCall;

  /* ----- data ----- */

  useEffect(() => {
    let active = true;
    Promise.allSettled([userService.getCategories(), userService.getDropdownData("call"), userService.getCallPackages()]).then(
      ([cats, dd, pk]) => {
        if (!active) return;
        const c = cats.status === "fulfilled" && cats.value?.data?.status ? cats.value.data.data : [];
        setCategories((Array.isArray(c) ? c : c?.categories || []).map((x) => ({ value: String(x.id), label: x.name })));
        const d = dd.status === "fulfilled" && dd.value?.data?.status ? dd.value.data.data : {};
        setJurisdictions((d.jurisdictions || []).map((x) => ({ value: String(x.id), label: x.name })));
        if (pk.status === "fulfilled" && pk.value?.data?.status) {
          const list = pk.value.data.data || [];
          const taken = !!pk.value.data.is_free_taken;
          // The free plan must be used first; once used only paid plans are selectable.
          const selectable = (p) => (taken ? !isFree(p) : isFree(p));
          setPackages(list);
          setFreeTaken(taken);
          setSelected(list.find((p) => selectable(p) && p.is_popular) || list.find(selectable) || null);
        }
        if ([cats, dd, pk].some((r) => r.status === "rejected")) toast.error("Some options couldn't be loaded.");
        setPackagesLoading(false);
      }
    );
    return () => {
      active = false;
    };
  }, []);

  /* ----- step 1 ----- */

  const caseReady = categoryId && jurisdictionId && description.trim() && accepted;

  const aiAssist = async () => {
    if (aiLoading || !description.trim()) return;
    setAiLoading(true);
    previousDescription.current = description;
    try {
      const res = await userService.draftCaseWithAI(description, categoryId, jurisdictionId);
      if (res?.data?.status && res.data.data?.suggestion) setDescription(res.data.data.suggestion);
      else toast.error(res?.data?.message || "AI assistant could not process your text.");
    } catch (err) {
      toast.error(apiErrorMessage(err, "AI assistant is unavailable right now."));
    } finally {
      setAiLoading(false);
    }
  };

  /* ----- step 3: media, payment, booking ----- */

  // Settle camera + mic before charging, or the card is held for a call that can't start.
  const requestMedia = async () => {
    setCheckingMedia(true);
    const media = await ensureMediaPermission();
    setCheckingMedia(false);
    if (media.ok) {
      setMediaPrompt(null);
      return true;
    }
    setMediaPrompt({ reason: media.reason, state: await getMediaPermissionState() });
    return false;
  };

  const proceed = async () => {
    if (!selected) return;
    if (!(await requestMedia())) return;
    if (isFree(selected)) startCall("", "free");
    else setPayOpen(true);
  };

  const caseDetails = () => ({
    category: categories.find((c) => c.value === categoryId)?.label || "",
    jurisdiction: jurisdictions.find((j) => j.value === jurisdictionId)?.label || "",
    description,
    plan: selected ? `${selected.duration_minutes} Minutes${selected.title ? ` · ${selected.title}` : ""}` : "",
  });

  const bookingFields = (transactionId, paymentStatus) => ({
    call_package_id: selected.id,
    category_id: categoryId,
    jurisdiction_id: jurisdictionId,
    case_description: description,
    amount_paid: selected.price,
    currency: selected.currency || "USD",
    transaction_id: transactionId,
    payment_status: paymentStatus,
  });

  const announceUser = () => socketService.emit("user-connected", { id: profile?.id, name: profile?.name });

  const startCall = async (transactionId, paymentStatus) => {
    setBooking(true);
    try {
      await broadcast(transactionId, paymentStatus);
    } catch (err) {
      // Release anything already booked so a held card isn't left hanging.
      const c = callRef.current;
      if (c) userService.endCall(c.callId).catch(() => {});
      toast.error(apiErrorMessage(err, err?.message || "Could not start the call."));
      resetCall();
    } finally {
      setBooking(false);
    }
  };

  const baseCall = (d, lawyer) => ({
    callId: d.callId,
    channel: d.channel,
    creds: { appId: d.agoraAppId, channel: d.channel, token: d.token, uid: d.uid },
    lawyer,
    user: d.user,
    durationMinutes: Number(selected?.duration_minutes) || 0,
    authToken: userAuth.getToken(),
    caseDetails: caseDetails(),
  });

  // Consultation: save the request, then broadcast it to every matched lawyer.
  const broadcast = async (transactionId, paymentStatus) => {
    const stored = await userService.storeCallHistory(bookingFields(transactionId, paymentStatus));
    if (!stored?.data?.status) throw new Error(stored?.data?.message || "Booking failed.");
    // Track the booking right away so a failure below can still release it.
    callRef.current = { callId: stored.data.data?.call_id };
    setLawyersFound(false);
    setPhase("searching");
    phaseRef.current = "searching";
    clearTimeout(giveUpTimer.current);
    giveUpTimer.current = setTimeout(giveUp, SEARCH_SECONDS * 1000);

    const res = await userService.initiateConsultation(stored.data.data?.call_id);
    if (!res?.data?.status) throw new Error(res?.data?.message || "Could not start the search.");
    const d = res.data.data;
    const call = { ...baseCall(d, null), matched: d.lawyers || [] };
    callRef.current = call;
    setActiveCall(call);
    setLawyersFound(true);
    announceUser();
    socketService.emit("consultation-initiate", {
      callId: d.callId,
      channel: d.channel,
      lawyer_ids: (d.lawyers || []).map((l) => l.id),
      user: d.user,
    });
  };

  /* ----- call lifecycle ----- */

  const resetCall = () => {
    clearTimeout(giveUpTimer.current);
    callRef.current = null;
    setActiveCall(null);
    setLawyersFound(false);
    setPhase("idle");
    phaseRef.current = "idle";
  };

  const applyAccepted = (lawyerId, payloadLawyer) => {
    clearTimeout(giveUpTimer.current);
    const c = callRef.current;
    if (!c) return;
    const fromList = (c.matched || []).find((l) => String(l.id) === String(lawyerId));
    const next = { ...c, lawyer: { ...(c.lawyer || {}), ...(payloadLawyer || {}), ...(fromList || {}) } };
    callRef.current = next;
    setActiveCall(next);
    setPhase("connected");
    phaseRef.current = "connected";
  };

  // Stop broadcasting and release the card hold. A lawyer can accept at the
  // same instant; the socket ack tells us if we lost that race, and then
  // we join the call instead of abandoning them in it.
  const release = (onDone) => {
    clearTimeout(giveUpTimer.current);
    const c = callRef.current;
    if (!c) return onDone();
    let settled = false;
    const stillOurs = () => callRef.current?.callId === c.callId && phaseRef.current === "searching";
    const teardown = () => {
      userService.endCall(c.callId).catch(() => {});
      onDone();
    };
    socketService.emit("consultation-cancel", { callId: c.callId }, (ack) => {
      if (settled) return;
      settled = true;
      if (ack?.status === "already-accepted") {
        if (stillOurs()) applyAccepted(ack.lawyer_id, null);
        return;
      }
      if (stillOurs()) teardown();
    });
    setTimeout(() => {
      if (settled) return;
      settled = true;
      if (stillOurs()) teardown();
    }, 4000);
    return undefined;
  };

  const cancelWaiting = () => release(resetCall);

  const giveUp = () => {
    if (phaseRef.current !== "searching") return;
    release(() => {
      resetCall();
      setPhase("unavailable");
      phaseRef.current = "unavailable";
    });
  };

  const hangUp = () => {
    const c = callRef.current;
    if (c) {
      socketService.emit("call-end", { callId: c.callId });
      userService.endCall(c.callId).catch(() => {});
    }
    resetCall();
    navigate("/user/call-history");
  };

  useEffect(() => {
    const offs = [
      socketService.subscribe("call-accepted", (p) => {
        if (phaseRef.current !== "searching") return;
        if (p?.callId && callRef.current && p.callId !== callRef.current.callId) return;
        applyAccepted(p?.lawyer_id, p?.lawyer);
      }),
      socketService.subscribe("call-ended", () => {
        if (phaseRef.current !== "connected") return;
        // The lawyer hung up — report it too so the call is settled either way.
        const c = callRef.current;
        if (c) userService.endCall(c.callId).catch(() => {});
        toast.info("The call has ended.");
        resetCall();
        navigate("/user/call-history");
      }),
    ];
    return () => offs.forEach((off) => off && off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => () => clearTimeout(giveUpTimer.current), []);

  /* ----- render ----- */

  if (directLawyerId) return <Navigate to={`/user/lawyers/${directLawyerId}?call=1`} replace />;

  const title = step === 1 ? "Choose Category" : step === 2 ? "Select Time" : "Connect";
  const back = () => (step > 1 ? setStep(step - 1) : navigate(-1));

  return (
    <div className="lwp-page usp-vc">
      <Seo title="Video Call with Lawyer | Legal Platform" noindex />
      <header className="lwp-page-head">
        <button type="button" className="lwp-back" onClick={back} aria-label="Back">
          <i className="bi bi-chevron-left" />
        </button>
        <div className="lwp-page-head-title">
          <h1>{title}</h1>
        </div>
      </header>

      <ol className="usp-steps">
        {STEPS.map(({ n, label }) => (
          <li key={n} className={`${step === n ? "active" : ""}${step > n ? " done" : ""}`}>
            <span className="usp-step-num">{step > n ? <i className="bi bi-check-lg" /> : n}</span>
            <span className="usp-step-label">{label}</span>
          </li>
        ))}
      </ol>

      <p className="usp-vc-intro">Connect with a verified lawyer on video call within minutes</p>

      {step === 1 && (
        <>
          <section className="usp-vc-card">
            <h3>
              1. Choose Legal Category <span className="usp-req">*</span>
            </h3>
            <PickerField
              label="Legal Category"
              hideLabel
              placeholder="Select Categories"
              options={categories}
              value={categoryId}
              onChange={setCategoryId}
            />
          </section>
          <section className="usp-vc-card">
            <h3>
              2. Choose Jurisdiction <span className="usp-req">*</span>
            </h3>
            <PickerField
              label="Jurisdiction"
              hideLabel
              placeholder="Select Jurisdiction"
              options={jurisdictions}
              value={jurisdictionId}
              onChange={setJurisdictionId}
            />
          </section>
          <section className="usp-vc-card">
            <h3>
              3. Describe your case briefly <span className="usp-req">*</span>
            </h3>
            <textarea
              className="usp-textarea"
              placeholder="Write a short description of your legal issue..."
              maxLength={1000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            {description.trim().split(/\s+/).filter(Boolean).length >= 2 && (
              <div className="usp-ai-row">
                <button type="button" className="lwp-pill-btn" onClick={aiAssist} disabled={aiLoading}>
                  <i className="bi bi-stars" />
                  <span>{aiLoading ? "Writing…" : "Write with AI"}</span>
                </button>
                {previousDescription.current && previousDescription.current !== description && (
                  <button
                    type="button"
                    className="usp-link-btn"
                    onClick={() => setDescription(previousDescription.current)}
                    disabled={aiLoading}
                  >
                    Undo
                  </button>
                )}
              </div>
            )}
          </section>
          <label className="lwp-check">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} />
            <span>
              Accept all <Link to="/privacy" target="_blank">Privacy policy</Link> and{" "}
              <Link to="/terms" target="_blank">Terms &amp; conditions</Link>.
            </span>
          </label>
          <button type="button" className="lwp-btn usp-vc-cta" disabled={!caseReady} onClick={() => setStep(2)}>
            Continue <i className="bi bi-arrow-right ms-2" />
          </button>
        </>
      )}

      {step === 2 && (
        <>
          {packagesLoading ? (
            <Spinner />
          ) : packages.length === 0 ? (
            <p className="lwp-empty">No call plans are available right now.</p>
          ) : (
            <div className="usp-plans">
              {packages.map((p) => {
                const free = isFree(p);
                const disabled = freeTaken ? free : !free;
                return (
                  <button
                    type="button"
                    key={p.id}
                    className={`usp-plan${selected?.id === p.id ? " selected" : ""}`}
                    onClick={() => setSelected(p)}
                    disabled={disabled}
                  >
                    <i className="bi bi-clock" />
                    <strong>{p.duration_minutes} Minutes</strong>
                    <span>{fmtPrice(p)}</span>
                    {disabled ? (
                      <small className="usp-plan-tag muted">{free ? "Already Used" : "Use Free First"}</small>
                    ) : (
                      p.is_popular && <small className="usp-plan-tag">Popular</small>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          <button type="button" className="lwp-btn usp-vc-cta" disabled={!selected} onClick={() => setStep(3)}>
            Continue <i className="bi bi-arrow-right ms-2" />
          </button>
        </>
      )}

      {step === 3 && selected && (
        <>
          <section className="usp-vc-card">
            <h3>Summary</h3>
            <dl className="lwp-detail-list">
              {[
                ["Category", caseDetails().category],
                ["Jurisdiction", caseDetails().jurisdiction],
                ["Plan", `${selected.duration_minutes} Minutes${selected.title ? ` · ${selected.title}` : ""}`],
                ["Total", fmtPrice(selected)],
              ].map(([k, v]) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{v}</dd>
                </div>
              ))}
            </dl>
          </section>
          {!isFree(selected) && (
            <p className="usp-pay-note">
              <i className="bi bi-shield-lock-fill" /> Your card is only held now — charged after your call ends. If no lawyer
              connects, the hold is released.
            </p>
          )}
          <button type="button" className="lwp-btn usp-vc-cta" onClick={proceed} disabled={checkingMedia || booking}>
            {checkingMedia || booking ? (
              <span className="spinner-border spinner-border-sm" />
            ) : (
              <>
                <i className="bi bi-camera-video-fill me-2" />
                {isFree(selected) ? "Start Call — Free" : `Pay & Connect — ${fmtPrice(selected)}`}
              </>
            )}
          </button>
        </>
      )}

      <StripeCardModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Confirm & Pay"
        amountLabel={selected ? fmtPrice(selected) : ""}
        submitLabel="Authorize & Connect"
        billing={{ name: profile?.name, email: profile?.email }}
        note="Your card is only held now — charged after your call ends."
        prepare={() => userService.consultationPayment(selected.price, selected.id)}
        onPaid={(pi) => startCall(pi.id, pi.status === "requires_capture" ? "authorized" : "paid")}
      />

      {mediaPrompt && (
        <MediaPermissionModal
          reason={mediaPrompt.reason}
          state={mediaPrompt.state}
          onRetry={async () => {
            const ok = await requestMedia();
            if (ok) proceed();
            return ok;
          }}
          onCancel={() => setMediaPrompt(null)}
        />
      )}

      {(phase === "searching" || phase === "unavailable") && (
        <FindingLawyer
          lawyers={activeCall?.matched || []}
          found={lawyersFound}
          timedOut={phase === "unavailable"}
          onCancel={cancelWaiting}
          onClose={() => setPhase("idle")}
        />
      )}

      {phase === "connected" && activeCall && <CallScreen call={activeCall} onEnd={hangUp} />}
    </div>
  );
};

export default UserVideoCall;
